/**
 * Opportunity CSV Bulk Import — Item 3(C)
 *
 * 4-step wizard:
 *   1. UPLOAD   — drag/drop or pick a .csv (max 5 MB / 5000 rows)
 *   2. MAP      — auto-suggested column → opportunity field, user can adjust
 *   3. VALIDATE — preview per-row status (ok / error) with row-level details
 *   4. CONFIRM  — final commit; shows created count + skipped reasons
 *
 * Strict contract:
 *   - DRAFT ONLY: no opportunities written until /commit is called
 *   - ADDITIVE: never modifies existing opportunities (creates fresh rows only)
 *   - USER CONFIRMS before commit
 */
import { useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  UploadCloud, FileSpreadsheet, ArrowLeft, ArrowRight, CheckCircle2,
  XCircle, AlertTriangle, Loader2, Trash2, RotateCcw, Download, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


const STEPS = ['Upload', 'Map columns', 'Validate', 'Duplicates', 'Confirm'];


function StepHeader({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="csv-import-stepper">
      {STEPS.map((label, idx) => {
        const num = idx + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
              done ? 'bg-emerald-600 text-white'
                   : active ? 'bg-[#B89B5E] text-white'
                   : 'bg-muted text-muted-foreground'
            }`}>
              {done ? <CheckCircle2 className="w-4 h-4" /> : num}
            </div>
            <div className={`text-sm ${active ? 'font-semibold' : 'text-muted-foreground'}`}>
              {label}
            </div>
            {idx < STEPS.length - 1 && (
              <div className="w-8 h-px bg-border" />
            )}
          </div>
        );
      })}
    </div>
  );
}


export default function OpportunityCSVImport() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [importId, setImportId] = useState(null);
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [availableFields, setAvailableFields] = useState([]);
  const [skipSentinel, setSkipSentinel] = useState('__skip__');
  const [mapping, setMapping] = useState({});

  const [validation, setValidation] = useState(null); // { summary, rows, mapping }
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [duplicates, setDuplicates] = useState(null); // { duplicate_count, total_rows, flagged_rows }
  const [skipRowSet, setSkipRowSet] = useState(new Set()); // user-chosen duplicate row indexes to skip

  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [checkingDupes, setCheckingDupes] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);


  // ─────────── reset ───────────

  const resetWizard = () => {
    setStep(1);
    setImportId(null);
    setFilename('');
    setHeaders([]);
    setSampleRows([]);
    setTotalRows(0);
    setAvailableFields([]);
    setMapping({});
    setValidation(null);
    setDuplicates(null);
    setSkipRowSet(new Set());
    setCommitResult(null);
    setSkipInvalid(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  // ─────────── step 1 — upload ───────────

  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a .csv file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5 MB limit');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${BACKEND}/api/opportunities/csv-import/upload`,
        formData,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      const d = res.data;
      setImportId(d.import_id);
      setFilename(d.filename);
      setHeaders(d.headers || []);
      setSampleRows(d.sample_rows || []);
      setTotalRows(d.total_rows || 0);
      setAvailableFields(d.available_fields || []);
      setSkipSentinel(d.skip_sentinel || '__skip__');
      setMapping(d.suggested_mapping || {});
      setStep(2);
      toast.success(`Loaded ${d.total_rows} rows from ${d.filename}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || e?.message || 'Upload failed');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }, [BACKEND, authHeaders]);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  };


  // ─────────── step 2 → step 3 — validate ───────────

  const requiredFields = useMemo(
    () => availableFields.filter((f) => f.required).map((f) => f.field),
    [availableFields],
  );

  const mappedFieldSet = useMemo(() => {
    const s = new Set();
    Object.values(mapping || {}).forEach((v) => {
      if (v && v !== skipSentinel) s.add(v);
    });
    return s;
  }, [mapping, skipSentinel]);

  const missingRequired = useMemo(
    () => requiredFields.filter((f) => !mappedFieldSet.has(f)),
    [requiredFields, mappedFieldSet],
  );

  const setColumnMapping = (csvCol, fieldOrSkip) => {
    setMapping((prev) => {
      // ensure single-target: if another csv col was mapped to this field, clear it
      const next = { ...prev, [csvCol]: fieldOrSkip };
      if (fieldOrSkip && fieldOrSkip !== skipSentinel) {
        Object.keys(next).forEach((k) => {
          if (k !== csvCol && next[k] === fieldOrSkip) next[k] = skipSentinel;
        });
      }
      return next;
    });
  };

  const handleValidate = async () => {
    if (missingRequired.length > 0) {
      toast.error(`Map the required field(s): ${missingRequired.join(', ')}`);
      return;
    }
    setValidating(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/opportunities/csv-import/${importId}/validate`,
        { mapping },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } },
      );
      setValidation(res.data);
      setStep(3);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Validation failed');
      toast.error(msg);
    } finally {
      setValidating(false);
    }
  };


  // ─────────── step 3 → step 4 — duplicate preflight ───────────

  const handleCheckDuplicates = async () => {
    setCheckingDupes(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/opportunities/csv-import/${importId}/preflight-duplicates`,
        { mapping },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } },
      );
      setDuplicates(res.data);
      // Auto-skip set: do NOT prefill — user explicitly chooses
      setSkipRowSet(new Set());
      if ((res.data.duplicate_count || 0) === 0) {
        // No duplicates found → jump straight to confirm
        toast.success('No duplicates found in your existing pipeline');
        setStep(5);
      } else {
        setStep(4);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Duplicate check failed');
      toast.error(msg);
    } finally {
      setCheckingDupes(false);
    }
  };

  const toggleSkipRow = (rowIndex) => {
    setSkipRowSet((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const skipAllFlagged = () => {
    if (!duplicates) return;
    setSkipRowSet(new Set(duplicates.flagged_rows.map((r) => r.row_index)));
  };

  const keepAllFlagged = () => {
    setSkipRowSet(new Set());
  };


  // ─────────── step 4 — commit ───────────

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/opportunities/csv-import/${importId}/commit`,
        {
          mapping,
          skip_invalid: skipInvalid,
          skip_row_indexes: Array.from(skipRowSet),
        },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } },
      );
      setCommitResult(res.data);
      setStep(6);
      toast.success(`Imported ${res.data.created} opportunities`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || `Row ${detail?.row_index || ''} failed validation`);
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  };


  // ─────────── sample CSV download ───────────

  const downloadSampleCSV = () => {
    const sample = [
      'company_name,contact_name,contact_email,contact_phone,property_type,deal_value,probability,region,description,tags',
      'Cintas Vancouver,Jane Smith,jane@cintas.com,555-0100,Uniform Workwear,75000,40,Pacific Northwest,Existing distress signal · contract renewal in Q2,renewal,uniform',
      'Hilton Whistler,Mark Wong,mark@hilton.com,604-555-0123,Hotel,120000,25,Pacific Northwest,New build expansion,hospitality,new-build',
      'Sysco Foods Toronto,,procurement@sysco.com,,Food Distribution,50000,15,Ontario,RFP open Q3,rfp',
    ].join('\n');
    const blob = new Blob([sample], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opportunities_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };


  // ─────────── render ───────────

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="csv-import-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Link to="/pipeline" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3 h-3" /> Back to pipeline
            </Link>
            <div className="flex items-center gap-2 mb-1">
              <FileSpreadsheet className="w-5 h-5 text-[#B89B5E]" />
              <h1 className="font-serif text-3xl sm:text-4xl">Bulk Import Opportunities</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Upload a CSV, map your columns, preview validation, then confirm. Existing opportunities are never modified.
            </p>
          </div>
          {step > 1 && (
            <Button
              variant="outline" size="sm" className="rounded-sm"
              onClick={resetWizard} data-testid="csv-import-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Start over
            </Button>
          )}
        </div>

        <StepHeader step={step} />

        {/* STEP 1 — UPLOAD */}
        {step === 1 && (
          <Card className="rounded-sm border" data-testid="csv-import-step-upload">
            <CardContent className="p-8 space-y-5">
              <div
                className="border-2 border-dashed rounded-sm p-10 text-center bg-muted/20 hover:bg-muted/30 transition cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="csv-import-dropzone"
              >
                <UploadCloud className="w-12 h-12 mx-auto text-[#B89B5E] mb-3" />
                <div className="font-serif text-2xl mb-1">Drop your CSV here</div>
                <div className="text-sm text-muted-foreground">
                  or click to choose a file · max 5 MB · max 5000 rows
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  data-testid="csv-import-file-input"
                />
              </div>

              {uploading && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Parsing CSV…
                </div>
              )}

              <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-muted-foreground border-t pt-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 text-[#B89B5E]" />
                  <div>
                    <div className="font-semibold">First row must contain column headers</div>
                    <div>We'll auto-suggest a mapping; you'll get a chance to review and edit it.</div>
                  </div>
                </div>
                <Button
                  variant="outline" size="sm" className="rounded-sm h-8"
                  onClick={downloadSampleCSV} data-testid="csv-import-sample-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download sample CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 2 — MAP */}
        {step === 2 && (
          <Card className="rounded-sm border" data-testid="csv-import-step-map">
            <CardContent className="p-5 md:p-6 space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Map columns</div>
                  <div className="font-serif text-2xl">
                    {totalRows} row{totalRows === 1 ? '' : 's'} from <span className="font-mono text-base">{filename}</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Required: <span className="font-semibold text-foreground">Company name</span>
                </div>
              </div>

              {missingRequired.length > 0 && (
                <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="csv-import-missing-required">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    Map the required field(s): <span className="font-semibold">{missingRequired.join(', ')}</span>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="csv-import-mapping-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                      <th className="py-2 pr-4">CSV column</th>
                      <th className="py-2 pr-4">Sample values</th>
                      <th className="py-2">Map to opportunity field</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map((h) => {
                      const samples = sampleRows.map((r) => r[h]).filter((v) => v !== undefined && v !== '').slice(0, 3);
                      return (
                        <tr key={h} className="border-b last:border-b-0 align-top">
                          <td className="py-2.5 pr-4 font-medium font-mono text-xs">{h}</td>
                          <td className="py-2.5 pr-4 text-xs text-muted-foreground">
                            {samples.length === 0
                              ? <span className="italic">empty</span>
                              : samples.map((v, i) => (
                                  <div key={i} className="truncate max-w-xs">{String(v)}</div>
                                ))}
                          </td>
                          <td className="py-2.5">
                            <Select
                              value={mapping[h] || skipSentinel}
                              onValueChange={(v) => setColumnMapping(h, v)}
                            >
                              <SelectTrigger
                                className="rounded-sm h-8 w-[260px]"
                                data-testid={`csv-import-map-${h}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={skipSentinel}>— Skip this column —</SelectItem>
                                {availableFields.map((f) => (
                                  <SelectItem key={f.field} value={f.field}>
                                    {f.label}{f.required ? ' *' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={resetWizard} className="rounded-sm" data-testid="csv-import-back-to-upload">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Choose a different file
                </Button>
                <Button
                  onClick={handleValidate}
                  disabled={validating || missingRequired.length > 0}
                  className="rounded-sm"
                  data-testid="csv-import-validate-btn"
                >
                  {validating
                    ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Validating…</>)
                    : (<>Validate {totalRows} row{totalRows === 1 ? '' : 's'} <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>)
                  }
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3 — VALIDATE */}
        {step === 3 && validation && (
          <Card className="rounded-sm border" data-testid="csv-import-step-validate">
            <CardContent className="p-5 md:p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Validation summary</div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <Badge className="rounded-sm bg-emerald-600 hover:bg-emerald-600 text-white" data-testid="csv-import-summary-valid">
                    {validation.summary.valid} valid
                  </Badge>
                  {validation.summary.invalid > 0 && (
                    <Badge className="rounded-sm bg-red-600 hover:bg-red-600 text-white" data-testid="csv-import-summary-invalid">
                      {validation.summary.invalid} invalid
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">
                    of {validation.summary.total} total
                  </span>
                </div>
              </div>

              <div className="rounded-sm border max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="py-2 px-3">#</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3">Company</th>
                      <th className="py-2 px-3">Contact</th>
                      <th className="py-2 px-3">Value</th>
                      <th className="py-2 px-3">Issues</th>
                    </tr>
                  </thead>
                  <tbody data-testid="csv-import-validate-rows">
                    {validation.rows.map((r) => (
                      <tr key={r.row_index} className={`border-b last:border-b-0 ${r.status === 'error' ? 'bg-red-50' : ''}`}>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{r.row_index}</td>
                        <td className="py-2 px-3">
                          {r.status === 'ok'
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            : <XCircle className="w-4 h-4 text-red-600" />}
                        </td>
                        <td className="py-2 px-3">{r.data.company_name || <span className="text-red-600">—</span>}</td>
                        <td className="py-2 px-3 text-xs">
                          {r.data.contact_name || ''}
                          {r.data.contact_email ? <div className="text-muted-foreground">{r.data.contact_email}</div> : null}
                        </td>
                        <td className="py-2 px-3 text-xs font-mono">
                          {r.data.deal_value ? `$${Number(r.data.deal_value).toLocaleString()}` : ''}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {r.errors.length > 0 && (
                            <div className="text-red-700">
                              {r.errors.map((e, i) => <div key={`e${i}`}>• {e}</div>)}
                            </div>
                          )}
                          {r.warnings.length > 0 && (
                            <div className="text-amber-700">
                              {r.warnings.map((w, i) => <div key={`w${i}`}>· {w}</div>)}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {validation.summary.invalid > 0 && (
                <div className="flex items-start gap-2 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
                  <Checkbox
                    id="skip-invalid"
                    checked={skipInvalid}
                    onCheckedChange={(v) => setSkipInvalid(Boolean(v))}
                    data-testid="csv-import-skip-invalid-checkbox"
                  />
                  <label htmlFor="skip-invalid" className="text-amber-900 cursor-pointer flex-1">
                    <div className="font-semibold">Skip invalid rows on import</div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      {skipInvalid
                        ? `${validation.summary.invalid} row(s) will be SKIPPED. ${validation.summary.valid} row(s) will be imported.`
                        : 'If unchecked, the entire import aborts on the first invalid row.'}
                    </div>
                  </label>
                </div>
              )}

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm" data-testid="csv-import-back-to-map">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to mapping
                </Button>
                <Button
                  onClick={handleCheckDuplicates}
                  disabled={checkingDupes || validation.summary.valid === 0}
                  className="rounded-sm"
                  data-testid="csv-import-check-dupes-btn"
                >
                  {checkingDupes
                    ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Checking for duplicates…</>)
                    : (<>Continue · check duplicates <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>)
                  }
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 4 — DUPLICATES */}
        {step === 4 && duplicates && duplicates.duplicate_count > 0 && (
          <Card className="rounded-sm border" data-testid="csv-import-step-duplicates">
            <CardContent className="p-5 md:p-6 space-y-5">
              <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">
                    {duplicates.duplicate_count} of {duplicates.total_rows} row{duplicates.total_rows === 1 ? '' : 's'} look like duplicates of existing opportunities
                  </div>
                  <div className="text-xs mt-1">
                    Existing opportunities are <span className="font-semibold">never modified</span>.
                    Choose which rows to skip — your choice is remembered until you confirm import.
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm" variant="outline" onClick={skipAllFlagged}
                  className="rounded-sm h-8" data-testid="csv-import-skip-all-flagged"
                >
                  Skip all flagged ({duplicates.duplicate_count})
                </Button>
                <Button
                  size="sm" variant="outline" onClick={keepAllFlagged}
                  className="rounded-sm h-8" data-testid="csv-import-keep-all-flagged"
                >
                  Keep all (import anyway)
                </Button>
                <Badge className="rounded-sm" variant="outline">
                  Skipping {skipRowSet.size} of {duplicates.duplicate_count}
                </Badge>
              </div>

              <div className="rounded-sm border max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="py-2 px-3 w-10">Skip</th>
                      <th className="py-2 px-3 w-12">#</th>
                      <th className="py-2 px-3">CSV row</th>
                      <th className="py-2 px-3">Matches existing opportunity</th>
                    </tr>
                  </thead>
                  <tbody data-testid="csv-import-duplicates-table">
                    {duplicates.flagged_rows.map((r) => (
                      <tr key={r.row_index} className={`border-b last:border-b-0 ${skipRowSet.has(r.row_index) ? 'bg-amber-50' : ''}`}>
                        <td className="py-2 px-3">
                          <Checkbox
                            checked={skipRowSet.has(r.row_index)}
                            onCheckedChange={() => toggleSkipRow(r.row_index)}
                            data-testid={`csv-import-dupe-toggle-${r.row_index}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{r.row_index}</td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{r.csv_company_name}</div>
                          {r.csv_contact_email && (
                            <div className="text-xs text-muted-foreground font-mono">{r.csv_contact_email}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 space-y-1">
                          {r.matches.map((m) => (
                            <div key={m.id} className="text-xs">
                              <div className="font-medium">{m.company_name}</div>
                              <div className="text-muted-foreground flex items-center gap-2 flex-wrap">
                                {m.contact_email && <span className="font-mono">{m.contact_email}</span>}
                                {m.stage && <Badge variant="outline" className="rounded-sm text-[10px]">{m.stage}</Badge>}
                                {m.match_reasons.map((reason) => (
                                  <Badge key={reason} className="rounded-sm text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100">{reason}</Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setStep(3)} className="rounded-sm" data-testid="csv-import-dupes-back">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to validation
                </Button>
                <Button
                  onClick={() => setStep(5)}
                  className="rounded-sm"
                  data-testid="csv-import-dupes-continue"
                >
                  Continue to confirm <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 5 — CONFIRM */}
        {step === 5 && validation && (
          <Card className="rounded-sm border" data-testid="csv-import-step-confirm">
            <CardContent className="p-5 md:p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Confirm import</div>
                <div className="font-serif text-3xl mt-1">
                  Import {Math.max(0, validation.summary.valid - skipRowSet.size)} opportunit{(validation.summary.valid - skipRowSet.size) === 1 ? 'y' : 'ies'}?
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-sm border p-4 bg-emerald-50">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700">Valid rows</div>
                  <div className="text-2xl font-semibold text-emerald-700 mt-1">{validation.summary.valid}</div>
                </div>
                <div className="rounded-sm border p-4 bg-amber-50">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700">Skipping (your choice)</div>
                  <div className="text-2xl font-semibold text-amber-700 mt-1">{skipRowSet.size}</div>
                  <div className="text-[10px] text-amber-700 mt-0.5">flagged duplicates</div>
                </div>
                <div className="rounded-sm border p-4 bg-red-50">
                  <div className="text-[10px] uppercase tracking-widest text-red-700">Invalid rows</div>
                  <div className="text-2xl font-semibold text-red-700 mt-1">{validation.summary.invalid}</div>
                  <div className="text-[10px] text-red-700 mt-0.5">{skipInvalid ? 'will be skipped' : 'aborts import'}</div>
                </div>
              </div>

              <div className="rounded-sm border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Existing opportunities will not be modified.</span>{' '}
                Each new row is created as a fresh opportunity tagged with this import batch ID for traceability.
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => duplicates && duplicates.duplicate_count > 0 ? setStep(4) : setStep(3)}
                  className="rounded-sm"
                  data-testid="csv-import-confirm-back"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
                </Button>
                <Button
                  onClick={handleCommit}
                  disabled={committing || (validation.summary.valid - skipRowSet.size) <= 0}
                  className="rounded-sm"
                  style={{ backgroundColor: '#B89B5E', color: 'white' }}
                  data-testid="csv-import-final-commit-btn"
                >
                  {committing
                    ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</>)
                    : (<>Confirm import <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>)
                  }
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 6 — DONE */}
        {step === 6 && commitResult && (
          <Card className="rounded-sm border" data-testid="csv-import-step-done">
            <CardContent className="p-8 space-y-5 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <div>
                <div className="font-serif text-3xl">Import complete</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Created <span className="font-semibold text-foreground">{commitResult.created}</span> opportunit{commitResult.created === 1 ? 'y' : 'ies'} ·
                  {commitResult.skipped > 0 ? ` skipped ${commitResult.skipped}` : ' all rows imported'}
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  Batch ID · {commitResult.batch_id}
                </div>
              </div>

              {commitResult.skipped_rows && commitResult.skipped_rows.length > 0 && (
                <div className="rounded-sm border bg-muted/20 p-4 text-left text-xs space-y-1.5 max-h-48 overflow-y-auto">
                  <div className="font-semibold mb-1 flex items-center gap-1 text-amber-700">
                    <Trash2 className="w-3.5 h-3.5" /> Skipped rows
                  </div>
                  {commitResult.skipped_rows.map((r) => (
                    <div key={r.row_index} className="text-muted-foreground">
                      Row {r.row_index}: {r.errors.join('; ')}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-center gap-2 pt-2">
                <Button variant="outline" onClick={resetWizard} className="rounded-sm" data-testid="csv-import-do-another">
                  Import another file
                </Button>
                <Button
                  onClick={() => navigate('/pipeline')}
                  className="rounded-sm"
                  data-testid="csv-import-go-pipeline"
                >
                  View pipeline <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
